/**
 * Partner-auth token store + authed fetch for the corporate/partner portal.
 *
 * SEPARATE from the staff session (src/lib/auth.ts): a partner JWT is scoped to
 * one partner organisation, persisted under its own key, and a 401 bounces to
 * the PARTNER login (/partner/login) — never the operator console. Keeping the
 * two stores independent means a logged-in partner and a logged-in operator
 * can't clobber each other, and staff endpoints stay on `authedFetch`.
 */

import { ApiError } from './api';
import { detailMessage } from './auth';

const TOKEN_KEY = 'cp_partner_token';
const PARTNER_KEY = 'cp_partner_id';
const LOGIN_PATH = '/partner/login';

export function getPartnerToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getPartnerId(): string | null {
  try {
    return sessionStorage.getItem(PARTNER_KEY);
  } catch {
    return null;
  }
}

export function isPartnerAuthed(): boolean {
  return getPartnerToken() !== null;
}

export function setPartnerSession(token: string, partnerId: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(PARTNER_KEY, partnerId);
  } catch {
    /* storage unavailable — token simply won't persist */
  }
}

export function clearPartnerSession(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(PARTNER_KEY);
  } catch {
    /* ignore */
  }
}

function redirectToPartnerLogin(): void {
  if (typeof window !== 'undefined' && window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
}

/** Fetch wrapper for partner-gated endpoints — mirrors authedFetch but uses the
 *  partner token and bounces to /partner/login on 401. */
export async function partnerFetch<T>(
  input: string,
  init: RequestInit = {},
  fallbackMessage = 'Something went wrong. Please try again.',
): Promise<T> {
  const token = getPartnerToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let resp: Response;
  try {
    resp = await fetch(input, { ...init, headers });
  } catch (e) {
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0,
      e,
    );
  }

  if (resp.status === 401) {
    clearPartnerSession();
    redirectToPartnerLogin();
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }

  if (!resp.ok) {
    let detail: unknown;
    try {
      detail = await resp.json();
    } catch {
      detail = undefined;
    }
    throw new ApiError(detailMessage(detail) ?? fallbackMessage, resp.status, detail);
  }

  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}
